import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HighlightOff from '@mui/icons-material/HighlightOff';
import {
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import Box from '@mui/material/Box';
import styled from 'styled-components';

import {
  DOWNLOAD_STATUS,
  DownloadProgressStack,
  ProgressStack,
  UPLOAD_STATUS,
  UploadProgressStack,
  useProgressStackContext,
} from '../../../../shared/context/ProgressStackContext';

const ProgressStackHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 20px 8px 20px;
  max-width: 400px;
  background: #2e5be0;
  border-radius: 15px 15px 0 0;
  color: white;
  font-weight: bold;
  box-sizing: border-box;
`;
const CircularProgressWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;
export const ProgressStackTray = () => {
  const { openTray, toggle, setToggle, stacks } = useProgressStackContext();
  const completed = stacks
    ? stacks.filter(
        stack =>
          stack.status === DOWNLOAD_STATUS.COMPLETED ||
          stack.status === UPLOAD_STATUS.COMPLETED,
      ).length
    : 0;

  return (
    <Box
      boxShadow={10}
      sx={{
        position: 'fixed',
        left: '30px',
        bottom: 0,
        width: '400px',
        maxWidth: 'calc(100vw - 80px)',
        maxHeight: '500px',
        borderRadius: '15px 15px 0 0',
        overflow: 'auto',
        zIndex: 1350,
      }}>
      {openTray && (
        <>
          <ProgressStackHeader>
            {completed} / {stacks?.length}
            <IconButton
              onClick={() => setToggle?.(!toggle)}
              sx={{ padding: 0 }}>
              {toggle ? (
                <ExpandMore sx={{ color: '#ffffff' }} />
              ) : (
                <ExpandLess sx={{ color: '#ffffff' }} />
              )}
            </IconButton>
          </ProgressStackHeader>
          <Collapse in={toggle}>
            <List
              component="div"
              disablePadding
              sx={{
                width: '400px',
                maxWidth: 'calc(100vw - 80px)',
                boxSizing: 'border-box',
                background: '#ffffff',
              }}>
              {!!stacks?.length &&
                stacks.map(stack => (
                  <ProgressStackListItem key={stack.id} stack={stack} />
                ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );
};

interface ProgressStackTrayProps {
  stack: ProgressStack;
}
const ProgressStackListItem = ({ stack }: ProgressStackTrayProps) => {
  return (
    <ListItem
      key={stack.id}
      divider
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '400px',
        maxWidth: 'calc(100vw - 80px)',
        paddingLeft: '20px',
        paddingRight: '20px',
        boxSizing: 'border-box',
      }}>
      <ListItemText
        primary={stack.message}
        disableTypography
        sx={{ flex: 1, fontSize: '14px', margin: 0 }}
      />
      {stack?.type === 'DOWNLOAD' ? (
        <DownloadProgressSpinner stack={stack} />
      ) : (
        <UploadProgressSpinner stack={stack} />
      )}
    </ListItem>
  );
};

interface DownloadProgressSpinnerProps {
  stack: DownloadProgressStack;
}
const DownloadProgressSpinner = ({ stack }: DownloadProgressSpinnerProps) => {
  return stack.status === DOWNLOAD_STATUS.COMPLETED ||
    stack.status === DOWNLOAD_STATUS.FAILED ? ( // 완료 또는 실패 시 삭제 버튼 보여주기
    <RemoveButton stackId={stack.id} />
  ) : stack.status === DOWNLOAD_STATUS.IN_PROGRESS ? (
    <CircularProgressWrapper>
      <CircularProgress
        variant="determinate"
        value={100}
        size={24}
        thickness={4}
        sx={{ color: '#a3a6ab' }}
      />
      <CircularProgress
        variant="determinate"
        value={
          ((stack.progress?.progress ?? 0) /
            (stack.progress?.totalImageCount ?? 1)) *
          100
        }
        size={24}
        thickness={4}
        sx={{
          color: '#2e5be0',
          position: 'absolute',
          left: 0,
        }}
      />
    </CircularProgressWrapper>
  ) : stack.status === DOWNLOAD_STATUS.COMPRESS ? (
    <CircularProgress size={24} sx={{ color: '#2e5be0' }} />
  ) : null;
};

interface UploadProgressSpinnerProps {
  stack: UploadProgressStack;
}
const UploadProgressSpinner = ({ stack }: UploadProgressSpinnerProps) => {
  const isUploading = stack.progress?.percentage !== 1;
  return stack.status === UPLOAD_STATUS.COMPLETED ||
    stack.status === UPLOAD_STATUS.FAILED ? ( // 완료 또는 실패 시 삭제 버튼 보여주기
    <RemoveButton stackId={stack.id} />
  ) : stack.status === UPLOAD_STATUS.IN_PROGRESS ? (
    isUploading ? ( // 진행중이면 진행형 progress 보여주기
      <CircularProgressWrapper>
        <CircularProgress
          variant="determinate"
          value={100}
          size={24}
          thickness={4}
          sx={{ color: '#a3a6ab' }}
        />
        <CircularProgress
          variant="determinate"
          value={(stack.progress?.percentage ?? 0) * 100}
          size={24}
          thickness={4}
          sx={{
            color: '#2e5be0',
            position: 'absolute',
            left: 0,
          }}
        />
      </CircularProgressWrapper>
    ) : (
      // 업로드 요청 마무리 중
      <CircularProgress size={24} sx={{ color: '#2e5be0' }} />
    )
  ) : null;
};

interface RemoveButtonProps {
  stackId: string;
}
const RemoveButton = ({ stackId }: RemoveButtonProps) => {
  const { removeStack } = useProgressStackContext();
  return (
    <IconButton
      onClick={() => removeStack?.(stackId)}
      sx={{ padding: 0, justifyContent: 'flex-end' }}>
      <HighlightOff />
    </IconButton>
  );
};
